import { useState } from "react";
import { Button, Empty, List, Modal, Popconfirm, Upload, message } from "antd";
import { PaperClipOutlined, UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  anexarComprovantePix,
  baixarComprovantePix,
  removerComprovantePix,
} from "@/services/pix";
import { ComprovantePixType, PixType } from "@/types/PixType";
import { errorHandler } from "@/utils/errorhandler";
import {
  AVISO_COMPROVANTE,
  FORMATOS_COMPROVANTE_ACEITOS,
  LIMITE_COMPROVANTE_BYTES,
  formatarTamanhoArquivo,
} from "@/utils/comprovante";

type Props = {
  pagamento: PixType | null;
  onClose: () => void;
  onChanged: () => void;
};

const formatDataHora = (data?: string) =>
  data ? dayjs(data).format("DD/MM/YYYY HH:mm") : "-";

const ComprovantesModal: React.FC<Props> = ({ pagamento, onClose, onChanged }) => {
  const [enviando, setEnviando] = useState(false);

  const enviarArquivo = async (arquivo: File) => {
    if (!pagamento) return;
    if (arquivo.size > LIMITE_COMPROVANTE_BYTES) {
      message.warning("O comprovante deve ter no máximo 10 MB.");
      return;
    }

    setEnviando(true);
    try {
      await anexarComprovantePix(pagamento.id, arquivo);
      message.success("Comprovante anexado com sucesso!");
      onChanged();
    } catch (error) {
      errorHandler(error);
    } finally {
      setEnviando(false);
    }
  };

  const abrirComprovante = async (comprovante: ComprovantePixType) => {
    try {
      const resposta = await baixarComprovantePix(comprovante.id);
      const tipo =
        (resposta.headers as any)?.["content-type"] ?? "application/octet-stream";
      const url = URL.createObjectURL(new Blob([resposta.data], { type: tipo }));
      window.open(url, "_blank", "noopener");
      // dá tempo de a aba nova carregar antes de soltar o blob
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      errorHandler(error);
    }
  };

  const removerComprovante = async (comprovante: ComprovantePixType) => {
    try {
      await removerComprovantePix(comprovante.id);
      message.success("Comprovante removido.");
      onChanged();
    } catch (error) {
      errorHandler(error);
    }
  };

  return (
    <Modal
      title={`Comprovantes — parcela ${pagamento?.parcela ?? ""} de ${
        pagamento?.nomeResponsavelFinanceiro ?? ""
      }`}
      open={pagamento !== null}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Fechar</Button>}
      width={640}
    >
      <div className="mb-4">
        <Upload
          accept={FORMATOS_COMPROVANTE_ACEITOS}
          showUploadList={false}
          beforeUpload={(arquivo) => {
            enviarArquivo(arquivo as File);
            // o upload é feito pelo service; o antd não deve postar sozinho
            return false;
          }}
        >
          <Button icon={<UploadOutlined />} loading={enviando}>
            Anexar comprovante
          </Button>
        </Upload>
        <div className="text-xs opacity-70 mt-2">{AVISO_COMPROVANTE}</div>
      </div>

      <List
        dataSource={pagamento?.comprovantes ?? []}
        locale={{
          emptyText: <Empty description="Nenhum comprovante anexado" />,
        }}
        renderItem={(comprovante) => (
          <List.Item
            actions={[
              <Button
                key="abrir"
                type="link"
                size="small"
                onClick={() => abrirComprovante(comprovante)}
              >
                Abrir
              </Button>,
              <Popconfirm
                key="remover"
                title="Remover este comprovante?"
                okText="Sim"
                cancelText="Não"
                onConfirm={() => removerComprovante(comprovante)}
              >
                <Button type="link" size="small" danger>
                  Remover
                </Button>
              </Popconfirm>,
            ]}
          >
            <List.Item.Meta
              avatar={<PaperClipOutlined />}
              title={comprovante.nomeArquivo}
              description={`${formatarTamanhoArquivo(comprovante.tamanhoBytes)} — ${formatDataHora(
                comprovante.dataUpload
              )} — ${comprovante.anexadoPorNome ?? ""}`}
            />
          </List.Item>
        )}
      />
    </Modal>
  );
};

export default ComprovantesModal;
